import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { ScrollText } from 'lucide-react';
import { Button, Card, Chip, FormField, Select } from '@/ui';
import { EMPTY_AUDIT_FILTERS, fetchPlatformAuditLog, type AuditFilters } from './api';
import { adminKeys, filtersKey } from './query-keys';
import { ADMIN_AUDIT_ACTION_CODES, ADMIN_AUDIT_ACTION_LABEL_KEYS } from './labels';
import { formatDateTime } from './format';
import { ListStatus, LoadMore } from './ListStatus';
import { flattenPages, usePagedQuery } from './use-paged-query';
import styles from './Admin.module.css';

/**
 * PLATFORM ISLEM GECMISI (`/yonetim/gecmis`).
 *
 * ============================= IKI KAYNAK BIR LISTE ========================
 * Bu kayit hem PLATFORM eylemlerini (`organizationId: null` — kullanici
 * pasiflestirme, rol degisimi, sifre sifirlama, isletme aktifligi) hem de
 * BUROLARIN kendi eylemlerini (uye ekleme, yetki verme…) tasir. Satirdaki
 * rozet bu ikisini ayirir; ayirmadan gosterilseydi "bunu kim yapti, hangi
 * yetkiyle?" sorusu cevapsiz kalirdi.
 *
 * ============================== SUZGECLER ==================================
 * Isletme ve aktor suzgeci ROTA DURUMUNDAN gelir (isletme/kullanici
 * detayindaki baglantilar), adres cubugundan DEGIL: ikisi de kimlik ve adres
 * cubugu gecmise, yer imlerine ve paylasilan baglantiya tasinir.
 */

/** Rota durumundan gelen suzgecler. Kimlikler pg int8 — STRING kalirlar. */
const filtersFromState = (state: unknown): Pick<AuditFilters, 'organizationId' | 'actorUserId'> => {
  if (typeof state !== 'object' || state === null) {
    return { organizationId: null, actorUserId: null };
  }
  const value = state as { organizationId?: unknown; actorUserId?: unknown };
  return {
    organizationId:
      typeof value.organizationId === 'string' && value.organizationId.length > 0
        ? value.organizationId
        : null,
    actorUserId:
      typeof value.actorUserId === 'string' && value.actorUserId.length > 0
        ? value.actorUserId
        : null,
  };
};

export function PlatformAuditLogPage() {
  const { t } = useTranslation();
  const location = useLocation();

  const scoped = useMemo(() => filtersFromState(location.state), [location.state]);

  const [filters, setFilters] = useState<AuditFilters>({
    ...EMPTY_AUDIT_FILTERS,
    ...scoped,
  });

  const key = filtersKey({
    actionCode: filters.actionCode,
    organizationId: filters.organizationId,
    actorUserId: filters.actorUserId,
  });

  const query = usePagedQuery(adminKeys.audit(key), (pageParam) =>
    fetchPlatformAuditLog({ filters, ...pageParam }),
  );

  const rows = flattenPages(query.data);
  const scopedActive = filters.organizationId !== null || filters.actorUserId !== null;
  const filtered = scopedActive || filters.actionCode !== null;

  return (
    <div className={styles.page}>
      <Card padding="md">
        <div className={styles.filterRow}>
          <FormField label={t('org.auditLog.filterAction')} className={styles.filterField}>
            <Select
              value={filters.actionCode ?? ''}
              placeholder={t('admin.filters.all')}
              options={ADMIN_AUDIT_ACTION_CODES.map((code) => ({
                value: code,
                // Sabit HARITA anahtar tutar; ceviri BURADA, render sirasinda.
                label: t(ADMIN_AUDIT_ACTION_LABEL_KEYS[code] ?? code),
              }))}
              onChange={(event) => {
                const value = event.target.value;
                setFilters((current) => ({
                  ...current,
                  actionCode: value === '' ? null : value,
                }));
              }}
            />
          </FormField>
          <div className={styles.filterActions}>
            <Button
              tone="ghost"
              onClick={() => {
                setFilters(EMPTY_AUDIT_FILTERS);
              }}
            >
              {t('filter.clearAll')}
            </Button>
          </div>
        </div>

        {scopedActive ? (
          <div className={styles.filterActions}>
            {filters.organizationId === null ? null : (
              <Chip tone="info">{t('admin.audit.scopedToOrganization')}</Chip>
            )}
            {filters.actorUserId === null ? null : (
              <Chip tone="info">{t('admin.audit.scopedToActor')}</Chip>
            )}
          </div>
        ) : null}
      </Card>

      <ListStatus
        isPending={query.isPending}
        isError={query.isError}
        error={query.error}
        isEmpty={query.isSuccess && rows.length === 0}
        emptyIcon={<ScrollText size={28} aria-hidden="true" />}
        emptyTitle={filtered ? t('list.noResults.title') : t('org.auditLog.empty')}
        {...(filtered ? { emptyDescription: t('list.noResults.description') } : {})}
        onRetry={() => {
          void query.refetch();
        }}
      />

      {rows.length > 0 ? (
        <Card padding="lg">
          <div className={styles.auditList}>
            {rows.map((row) => {
              const labelKey = ADMIN_AUDIT_ACTION_LABEL_KEYS[row.actionCode];
              return (
                <div key={row.id} className={styles.auditRow}>
                  <div className={styles.badges}>
                    {/* Bilinmeyen kod HAM basilir: yeni bir sunucu surumu bu
                        listeyi bos birakmamali. */}
                    <span className={styles.auditAction}>
                      {labelKey === undefined ? row.actionCode : t(labelKey)}
                    </span>
                    {/* organizationId NULL = platform seviyesi. */}
                    <Chip tone={row.organizationId === null ? 'warning' : 'neutral'} size="sm">
                      {row.organizationId === null
                        ? t('admin.audit.platformScope')
                        : (row.organizationName ?? t('admin.audit.organizationScope'))}
                    </Chip>
                  </div>
                  <span className={styles.muted}>
                    {row.actorName || t('members.unknownUser')} · {formatDateTime(row.createdAt)}
                  </span>
                  {row.permissionCount === null ? null : (
                    <span className={styles.muted}>
                      {row.permissionCount} {t('org.auditLog.permissionsGranted')}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          <LoadMore
            hasNextPage={query.hasNextPage}
            isFetchingNextPage={query.isFetchingNextPage}
            onLoadMore={() => {
              void query.fetchNextPage();
            }}
          />
        </Card>
      ) : null}
    </div>
  );
}
